import { useState, useRef, useEffect, useCallback } from 'react';
import { IoSend } from 'react-icons/io5';
import { FaSpinner } from 'react-icons/fa';
import { analyzeSurveyStream } from '../../api/survey-explorer/surveyExplorerApi';
import { createSharedResult, getSharedResults, deleteSharedResult } from '../../api/survey-explorer/sharedResultsApi';
import { SharedResultCard } from './SharedResultCard';
import { MyResultCard } from './MyResultCard';
import { SuggestionsSection } from './SuggestionsSection';
import { useMsal } from '@azure/msal-react';

export const SurveyExplorerPage = () => {
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [threadId] = useState(() => {
    const saved = localStorage.getItem('surveyExplorerV3_threadId');
    return saved ? JSON.parse(saved) : null;
  });
  const [agentOutputs, setAgentOutputs] = useState(() => {
    const saved = localStorage.getItem('surveyExplorerV3_agentOutputs');
    const results = saved ? JSON.parse(saved) : [];
    return results.map(result => ({
      ...result,
      id: result.id || 'result_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    }));
  });
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [streamingDescription, setStreamingDescription] = useState('');

  const [expandedStatistics, setExpandedStatistics] = useState(() => {
    const saved = localStorage.getItem('surveyExplorerV3_expandedStatistics');
    return saved ? JSON.parse(saved) : {};
  });
  const [publicResults, setPublicResults] = useState(() => {
    const saved = localStorage.getItem('surveyExplorerV3_publicResults');
    return saved ? JSON.parse(saved) : {};
  });
  
  const cleanupRef = useRef(null);
  
  const { instance, accounts } = useMsal();



  const [activeTab, setActiveTab] = useState('shared');
  const [sharedResults, setSharedResults] = useState([]);
  const [sharedResultsLoading, setSharedResultsLoading] = useState(false);
  const [sharedResultsError, setSharedResultsError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreResults, setHasMoreResults] = useState(true);
  const [togglingResults, setTogglingResults] = useState({});
  const [currentUser, setCurrentUser] = useState(null);

  const generateResultId = () => {
    return 'result_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };

  const acquireToken = useCallback(async () => {
    try {
      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts available for token acquisition');
      }

      const accessTokenRequest = {
        scopes: ['user.read'],
        account: accounts[0]
      };

      const accessTokenResponse = await instance.acquireTokenSilent(accessTokenRequest);
      setAccessToken(accessTokenResponse.accessToken);

      if (accounts && accounts.length > 0) {
        setCurrentUser({
          id: accounts[0].homeAccountId || accounts[0].localAccountId,
          localAccountId: accounts[0].localAccountId,
          homeAccountId: accounts[0].homeAccountId,
          username: accounts[0].username,
          name: accounts[0].name || accounts[0].username
        });
      }

      return accessTokenResponse.accessToken;
    } catch (error) {
      console.error('Error acquiring access token:', error);
      setError('Failed to acquire access token. Please refresh the page.');
      throw error;
    }
  }, [accounts, instance]);

  const loadSharedResults = useCallback(async (page = 1, reset = false) => {
    if (!accessToken) return;

    setSharedResultsLoading(true);
    setSharedResultsError(null);

    try {
      const response = await getSharedResults({ page, limit: 20 }, accessToken);

      if (reset || page === 1) {
        setSharedResults(response.results);
      } else {
        setSharedResults(prev => [...prev, ...response.results]);
      }

      setHasMoreResults(response.has_next);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error loading shared results:', error);
      setSharedResultsError(error.message);
    } finally {
      setSharedResultsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (accounts && accounts.length > 0) {
      acquireToken();
    }
  }, [accounts, acquireToken]);

  useEffect(() => {
    if (activeTab === 'shared' && accessToken) {
      loadSharedResults(1, true);
    }
  }, [activeTab, accessToken, loadSharedResults]);

  useEffect(() => {
    localStorage.setItem('surveyExplorerV3_agentOutputs', JSON.stringify(agentOutputs));
  }, [agentOutputs]);

  useEffect(() => {
    localStorage.setItem('surveyExplorerV3_expandedStatistics', JSON.stringify(expandedStatistics));
  }, [expandedStatistics]);

  useEffect(() => {
    if (threadId) {
      localStorage.setItem('surveyExplorerV3_threadId', JSON.stringify(threadId));
    }
  }, [threadId]);

  useEffect(() => {
    localStorage.setItem('surveyExplorerV3_publicResults', JSON.stringify(publicResults));
  }, [publicResults]);

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  const handleSubmit = async (inputValue = null) => {
    const queryText = String(inputValue || userInput || '');
    
    if (!queryText.trim()) return;
    
    try {
      await acquireToken();
    } catch (tokenError) {
      console.error('Failed to acquire token:', tokenError);
      return;
    }
    
    if (cleanupRef.current) {
      cleanupRef.current();
    }
    
    setIsLoading(true);
    setError(null);
    setStatusMessage('');
    setStreamingDescription('');
    
    const resultId = generateResultId();
    const newResult = {
      id: resultId,
      query: queryText,
      outputs: [],
      isInProgress: true
    };
    setAgentOutputs(prev => [newResult, ...prev]);
    
    try {
      const cleanup = analyzeSurveyStream(
        queryText,
        threadId,
        accessToken,
        (data) => {
          if (data.type === 'custom_message') {
            const content = data.content;
            if (typeof content === 'object' && content.description_chunk) {
              setStreamingDescription(prev => prev + content.description_chunk);
            } else if (typeof content === 'object' && content.message) {
              setStatusMessage(content.message);
            } else if (typeof content === 'string') {
              setStatusMessage(content);
            } else {
              setStatusMessage('Processing...');
            }
          } else if (data.type === 'agent_output') {
            setStreamingDescription('');
            setAgentOutputs(prev => {
              const updated = [...prev];
              if (updated.length > 0) {
                updated[0] = {
                  ...updated[0],
                  outputs: [...updated[0].outputs, data.content]
                };
              }
              return updated;
            });
          }
        },
        (error) => {
          console.error('Stream error:', error);
          
          const hasReceivedData = agentOutputs.length > 0 && agentOutputs[0].outputs && agentOutputs[0].outputs.length > 0;
          
          if (error.message.includes('Network connection was interrupted') && hasReceivedData) {
            setIsLoading(false);
            setStatusMessage('');
            setAgentOutputs(prev => {
              const updated = [...prev];
              if (updated.length > 0) {
                const { isInProgress, ...result } = updated[0];
                updated[0] = result;
              }
              return updated;
            });
          } else {
            setError(error.message || 'An error occurred while analyzing the survey');
            setIsLoading(false);
            setStatusMessage('');
            setAgentOutputs(prev => {
              const updated = [...prev];
              if (updated.length > 0) {
                const { isInProgress, ...result } = updated[0];
                updated[0] = result;
              }
              return updated;
            });
          }
        },
        () => {
          setIsLoading(false);
          setStatusMessage('');
          setAgentOutputs(prev => {
            const updated = [...prev];
            if (updated.length > 0) {
              const { isInProgress, ...result } = updated[0];
              updated[0] = result;
            }
            return updated;
          });
        }
      );
      
      cleanupRef.current = cleanup;
      
    } catch (err) {
      console.error('Error starting survey analysis:', err);
      setError(err.message || 'An error occurred while starting the analysis');
      setIsLoading(false);
      setStatusMessage('');
      // Mark the current result as completed (remove isInProgress flag) if it was added
      setAgentOutputs(prev => {
        const updated = [...prev];
        if (updated.length > 0 && updated[0].isInProgress) {
          const { isInProgress, ...result } = updated[0];
          updated[0] = result;
        }
        return updated;
      });
    }
  };
  
  const handleSuggestionClick = (suggestion) => {
    setUserInput(suggestion);
    // Send the suggestion text directly instead of relying on state update
    handleSubmit(suggestion);
  };

  const removeResult = (resultId) => {
    setAgentOutputs(prev => prev.filter(result => result.id !== resultId));
    // Clean up all related state for this result
    setExpandedStatistics(prev => {
      const newExpanded = { ...prev };
      delete newExpanded[resultId];
      return newExpanded;
    });
    setPublicResults(prev => {
      const newPublicResults = { ...prev };
      delete newPublicResults[resultId];
      return newPublicResults;
    });
    setTogglingResults(prev => {
      const newTogglingResults = { ...prev };
      delete newTogglingResults[resultId];
      return newTogglingResults;
    });
  };

  const toggleResultPublic = async (resultId) => {
    if (!accessToken) {
      setError('Authentication required');
      return;
    }

    if (togglingResults[resultId]) {
      return; // Already toggling this result
    }

    const result = agentOutputs.find(r => r.id === resultId);
    if (!result) {
      setError('Result not found');
      return;
    }

    const isCurrentlyPublic = publicResults[resultId];
    
    setTogglingResults(prev => ({ ...prev, [resultId]: true }));
    
    try {
      if (!isCurrentlyPublic) {
        // Transform outputs to API format
        const transformedOutputs = [];
        let orderIndex = 0;

        result.outputs.forEach(output => {
          // Handle new format with generate_chart and write_description
          if (output.generate_chart && output.generate_chart.chart) {
            transformedOutputs.push({
              output_type: "chart",
              content: JSON.stringify({ chart: output.generate_chart.chart }),
              order_index: orderIndex++
            });
          }
          if (output.write_description && output.write_description.description) {
            transformedOutputs.push({
              output_type: "description", 
              content: JSON.stringify({ description: output.write_description.description }),
              order_index: orderIndex++
            });
          }
          
          // Handle legacy format for backwards compatibility
          if (output.chart) {
            transformedOutputs.push({
              output_type: "chart",
              content: JSON.stringify({ chart: output.chart }),
              order_index: orderIndex++
            });
          }
          if (output.description) {
            transformedOutputs.push({
              output_type: "description",
              content: JSON.stringify({ description: output.description }),
              order_index: orderIndex++
            });
          }
          if (output.fetch_statistics) {
            transformedOutputs.push({
              output_type: "statistics",
              content: JSON.stringify({ statistics: output.fetch_statistics.statistics }),
              order_index: orderIndex++
            });
          }
        });

        // Share the result
        const sharedResult = await createSharedResult({
          query: result.query,
          outputs: transformedOutputs
        }, accessToken);
        
        // Update local state with shared result ID
        setPublicResults(prev => ({
          ...prev,
          [resultId]: sharedResult.id
        }));
      } else {
        // Unshare the result - always toggle to private locally, ignore backend errors
        setPublicResults(prev => ({
          ...prev,
          [resultId]: false
        }));
        
        try {
          await deleteSharedResult(isCurrentlyPublic, accessToken);
        } catch (error) {
          // Silently ignore deletion errors
        }
      }
    } catch (error) {
      if (!isCurrentlyPublic) {
        console.error('Error sharing result:', error);
        setError(`Failed to share result: ${error.message}`);
      }
    } finally {
      setTogglingResults(prev => ({ ...prev, [resultId]: false }));
    }
  };

  const handleLikeToggle = (resultId, isLiked) => {
    setSharedResults(prev => 
      prev.map(result => {
        if (result.id === resultId) {
          const wasLiked = result.user_liked;
          const currentCount = result.like_count || 0;
          
          let newCount = currentCount;
          if (isLiked && !wasLiked) {
            newCount = currentCount + 1;
          } else if (!isLiked && wasLiked) {
            newCount = Math.max(currentCount - 1, 0);
          }
          
          return {
            ...result,
            user_liked: isLiked,
            like_count: newCount
          };
        }
        return result;
      })
    );
  };

  const loadMoreSharedResults = () => {
    if (!sharedResultsLoading && hasMoreResults) {
      loadSharedResults(currentPage + 1, false);
    }
  };

  const handleSharedResultDelete = (resultId) => {
    const deletedSharedResult = sharedResults.find(result => result.id === resultId);
    
    setSharedResults(prev => prev.filter(result => result.id !== resultId));
    
    if (deletedSharedResult) {
      const matchingPrivateResult = agentOutputs.find(result => 
        result.query === deletedSharedResult.query
      );
      
      if (matchingPrivateResult) {
        setPublicResults(prev => ({
          ...prev,
          [matchingPrivateResult.id]: false
        }));
      }
    }
  };

  const handleToggleStatistics = (resultId) => {
    setExpandedStatistics(prev => ({
      ...prev,
      [resultId]: !prev[resultId]
    }));
  };

  const handleUseQuery = (query) => {
    setActiveTab('private');
    setUserInput(query);
    // Focus the input field after a short delay to ensure tab switch is complete (or immediate if already on private tab)
    const delay = activeTab === 'private' ? 0 : 100;
    setTimeout(() => {
      const inputField = document.querySelector('input[type="text"]');
      if (inputField) {
        inputField.focus();
        inputField.setSelectionRange(inputField.value.length, inputField.value.length);
      }
    }, delay);
  };

  const renderAgentOutput = (result, index) => {
    return (
              <MyResultCard
          key={result.id}
          result={result}
          index={index}
          totalResults={agentOutputs.length}
          expandedStatistics={expandedStatistics}
          publicResults={publicResults}
          togglingResults={togglingResults}
          cleanupRef={cleanupRef}
          onToggleStatistics={handleToggleStatistics}
          onTogglePublic={toggleResultPublic}
          onRemoveResult={removeResult}
          onUseQuery={handleUseQuery}
          setIsLoading={setIsLoading}
          setStatusMessage={setStatusMessage}
          streamingDescription={result.isInProgress ? streamingDescription : ''}
        />
    );
  };

  return (
    <div className='page-background min-h-screen flex flex-col items-center'>

      <div className={`w-full flex flex-col px-4 max-w-6xl mx-auto mt-20`}>

        <div className="mb-6 flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('shared')}
            className={`py-4 px-6 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'shared'
                ? 'text-green-600 border-green-600'
                : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Shared Results
          </button>
          <button
            onClick={() => setActiveTab('private')}
            className={`py-4 px-6 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'private'
                ? 'text-green-600 border-green-600'
                : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            My Results
          </button>
        </div>


        {activeTab === 'private' && (
          <>
            <SuggestionsSection
              onSuggestionClick={handleSuggestionClick}
            />
            
            {/* Input Section */}
            <div className='bg-white rounded-lg shadow p-6 w-full mb-6'>
              <div className='flex items-center'>
                <input
                  type='text'
                  placeholder={isLoading ? 'Analyzing...' : 'Ask anything about the survey data...'}
                  className='flex-grow outline-none text-gray-700 p-3 border rounded-lg'
                  value={userInput}
                  disabled={isLoading}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && userInput.trim()) {
                      handleSubmit();
                    }
                  }}
                />
                <button
                  disabled={!userInput.trim() || isLoading}
                  onClick={() => handleSubmit()}
                  className={`
                    ${userInput.trim() && !isLoading ? 'bg-green-500 hover:bg-green-400' : 'bg-gray-200'} 
                    rounded-full p-3 ml-3 transition
                  `}
                >
                  {isLoading ? (
                    <FaSpinner className="animate-spin h-5 w-5 text-white" />
                  ) : (
                    <IoSend
                      className={`h-5 w-5 ${
                        userInput.trim() && !isLoading ? 'text-white' : 'text-gray-400'
                      }`}
                    />
                  )}
                </button>
              </div>
              
              {/* Real-time Status Message */}
              {isLoading && statusMessage && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <FaSpinner className="animate-spin h-4 w-4 text-blue-500 mr-2" />
                    <span className="text-blue-700 text-sm">{statusMessage}</span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        
        {/* Results Section */}
        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        {activeTab === 'private' ? (
          <>
            {/* Private results */}
            {agentOutputs.map((result, index) => renderAgentOutput(result, index))}
            
            {agentOutputs.length === 0 && !error && !isLoading && (
              <div className="bg-white rounded-lg shadow p-6 w-full">
                <p className="text-gray-500 text-center">
                  Enter a question about the survey data to see real-time analysis results
                </p>
              </div>
            )}
          </>
        ) : (
          // Shared results tab
          <div className="w-full">
            {sharedResultsError && (
              <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6">
                {sharedResultsError}
                <button 
                  onClick={() => loadSharedResults(1, true)}
                  className="ml-2 underline hover:no-underline"
                >
                  Try again
                </button>
              </div>
            )}

            {sharedResults.length === 0 && !sharedResultsLoading && !sharedResultsError && (
              <div className="bg-white rounded-lg shadow p-6 w-full">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Shared Results Yet</h3>
                  <p className="text-gray-500">
                    Be the first to share an analysis result! Switch to "My Results" and make a result public.
                  </p>
                </div>
              </div>
            )}

            {sharedResults.map((result) => (
              <SharedResultCard
                key={result.id}
                result={result}
                accessToken={accessToken}
                onLikeToggle={handleLikeToggle}
                currentUser={currentUser}
                onDelete={handleSharedResultDelete}
                onUseQuery={handleUseQuery}
              />
            ))}

            {sharedResultsLoading && (
              <div className="flex justify-center py-8">
                <FaSpinner className="animate-spin h-6 w-6 text-gray-400" />
              </div>
            )}

            {hasMoreResults && !sharedResultsLoading && sharedResults.length > 0 && (
              <div className="flex justify-center py-4">
                <button
                  onClick={loadMoreSharedResults}
                  className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  Load More Results
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}; 